import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { io } from "socket.io-client";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ user, children }) => {
  const [socket, setSocket] = useState(null);
  const fallbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  // Use explicit REACT_APP_SOCKET_URL, or fall back to origin, never use /api path
  const socketBase =
    process.env.REACT_APP_SOCKET_URL ||
    fallbackOrigin ||
    "http://localhost:5001";
    
  useEffect(() => {
    // Debug: log when useEffect runs and current user id
    try {
      console.info("[WS] useEffect trigger", { userId: user?.id, socketBase });
    } catch (e) {}

    // Don't connect until we have a user ID
    if (!user?.id) {
      try {
        console.info("[WS] skipping connect - no user id available yet");
      } catch (e) {}
      return;
    }

    // Create the socket connection, passing the userId for room membership
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.info("[WS] connecting with timezone", { userId: user.id, userTimeZone });
    const socketInstance = io(socketBase, {
      withCredentials: true,
      auth: { 
        userId: user.id,
        timeZone: userTimeZone
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    setSocket(socketInstance);

    // Basic lifecycle logging to help while learning
    socketInstance.on("connect", () => {
      console.info("[WS] connected", socketInstance.id);
      socketInstance.emit("join", { userId: user.id });

      // Always attempt to join both the user room and the linguist room for this id.
      // Joining linguist:<id> is harmless for members and ensures linguist-targeted
      // socket events reach the client even if Role metadata is missing.
      try {
        socketInstance.emit("join", { linguistId: user.id });
        console.info(`[WS] requested join linguist:${user.id}`);
      } catch (err) {
        console.warn("[WS] failed to request join linguist room", err?.message || err);
      }

      // Expose socket to window for local debugging (dev only)
      try {
        if (typeof window !== "undefined") {
          window.__socketInstance = socketInstance;
        }
      } catch (e) {
        /* ignore */
      }
    });

    socketInstance.on("disconnect", (reason) => {
      console.info("[WS] disconnected", reason);
    });

    socketInstance.on("connect_error", (err) => {
      console.error("[WS] connect_error", err.message);
    });

    socketInstance.on("reconnect_attempt", (attemptNumber) => {
      console.info("[WS] reconnect attempt", attemptNumber);
    });

    socketInstance.on("reconnect_failed", () => {
      console.error("[WS] reconnection failed after all attempts");
    });

    // Clean up on unmount
    return () => {
      try {
        if (typeof window !== "undefined" && window.__socketInstance === socketInstance) {
          delete window.__socketInstance;
        }
      } catch (e) {}
      socketInstance.disconnect();
    };
  }, [user?.id, socketBase]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
